import { HttpStatusCode } from 'axios';
import { DeepKeys } from '@tanstack/react-table';
import { BaseEntity } from '@/api/EntityApi';
import { MIME_TYPES } from '@mantine/dropzone';
import LANGUAGES from '@/configs/languages';

export enum ErrorCode {
	GENERIC = 'genericError',
	USER_NOT_FOUND = 'userNotFound',
	USER_NOT_ACTIVE = 'userNotActive',
	USER_ALREADY_EXISTS = 'userAlreadyExists',
	DUPLICATED_EMAIL = 'duplicatedEmail',
	MISSING_FIELD = 'missingField',
	EMAIL_NOT_FOUND = 'emailNotFound',
	INVALID_EMAIL = 'emailIsNotValid',
	INVALID_PASSWORD = 'invalidPassword',
	INVALID_PHONE_NUMBER = 'phoneNumberIsNotValid',
	PASSWORD_TOO_WEAK = 'passwordTooWeak',
	DIFFERENT_PASSWORDS = 'differentPasswords',
	DIFFERENT_EMAILS = 'differentEmails',
	INVALID_REFRESH_TOKEN = 'invalidRefreshToken',
	REFRESH_TOKEN_EXPIRED = 'refreshTokenExpired',
	REFRESH_TOKEN_MALFORMED = 'refreshTokenMalformed',
	INVALID_TOKEN = 'invalidToken',
	INVALID_HASH = 'invalidHash',
	INVALID_POSTAL_CODE = 'invalidPostalCode',
	TOO_MANY_REQUESTS = 'tooManyRequests',
	INVALID_EMAIL_DOMAIN = 'invalidEmailDomain',
	REGISTRATION_FAILED = 'registrationFailed',
}

export type Language = keyof typeof LANGUAGES;
export type ForeignLanguage = Exclude<Language, 'PL'>;
export type Translations<TData> = Record<ForeignLanguage, TData>;

export type SortOrder = 'ASC' | 'DESC';

export interface Sort<TData extends BaseEntity = any> {
	key: DeepKeys<TData> | null;
	order: SortOrder;
}

export interface ApiQueryOptions<TData extends BaseEntity = any> {
	page: number;
	limit: number;
	search?: string;
	sortBy: Sort<TData>;
}

export interface ApiPaginatedResponse<TData extends {} = {}> {
	data: TData[];
	meta: {
		currentPage: number;
		itemsPerPage: number;
		totalItems: number;
		totalPages: number;
	};
}

export interface ApiFormattedResponseData<TData extends {} = {}> {
	data: TData[];
	totalPages: number;
}

export type CustomFilters = Record<string, unknown>;

export interface Attachment {
	id: UUID;
	path: string;
	name: string;
	size: number;
	mimetype: AppMimeType;
}

export type UUID = ReturnType<typeof crypto.randomUUID>;

export type ApiErrorMessage = ErrorCode | string;

export interface ApiError {
	message: ApiErrorMessage[];
	status: HttpStatusCode;
}

export interface AppError {
	status: HttpStatusCode;
	stack: ErrorCode | string;
}

export type AppMimeType = (typeof MIME_TYPES)[keyof typeof MIME_TYPES];

export enum RelationType {
	ATTRIBUTE_VALUE = 'ATTRIBUTE_VALUE',
}

export enum RelationAction {
	HIDE = 'HIDE',
	SHOW = 'SHOW',
}

export interface Relation {
	type: RelationType;
	objectId: UUID;
	action: RelationAction;
}

export enum ExportType {
	PDF = 'pdf',
	XLSX = 'xlsx',
}
